/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import type {
  IApiConfiguration,
  IApiDeleteConfiguration,
  IApiGetConfiguration,
  IApiPatchConfiguration,
  IApiPostConfiguration,
  IApiPutConfiguration,
} from './interface';

export class APICreate {
  private _configuration: IApiConfiguration = {};

  private _client() {
    return axios;
  }

  public config(config: IApiConfiguration) {
    this._configuration = config;
  }

  public toString() {
    return {
      config: this._configuration,
    };
  }

  private isExistService(serviceName: string) {
    return !!this._configuration[serviceName];
  }

  private async createParam<P, H>(
    serviceName: string,
    method: string,
    path: string,
    config: IApiGetConfiguration<P, H>,
  ) {
    if (!this.isExistService(serviceName)) {
      throw new Error(`Service ${serviceName} not found`);
    }
    const customRootHeader = this._configuration[serviceName].customHeaders ?? undefined;
    const baseUrl = this._configuration[serviceName].base_url.replace(/\/$/, '');
    const withCredentials = this._configuration[serviceName].withCredentials ?? false;
    const custom_header_obj =
      typeof customRootHeader === 'function' ? await customRootHeader() : undefined;

    const params: any = {
      method: method,
      url: path,
      baseURL: baseUrl,
      headers: {},
      data: null,
      responseType: config.responseType ?? 'json',
      params: { ...(config.params ?? {}) },
      timeout: config.timeout ?? 0,
      cancelToken: null,
      withCredentials: withCredentials,
    };

    const initParams = Object.assign({ ...params }, config);

    const libraryHeaders: any = {};
    if (initParams.data) {
      if (typeof FormData === 'function' && initParams.data instanceof FormData) {
        libraryHeaders['Content-Type'] = 'multipart/form-data';
        params.data = initParams.data;
      } else {
        libraryHeaders['Content-Type'] = 'application/json; charset=UTF-8';
        params.data = JSON.stringify(initParams.data);
      }
    }

    initParams.headers = {
      ...libraryHeaders,
      ...custom_header_obj,
      ...(config.headers ?? {}),
    };

    return initParams;
  }

  private async ajax<P, H>(
    serviceName: string,
    method: string,
    path: string,
    config: IApiGetConfiguration<P, H>,
  ) {
    const initParams: any = await this.createParam(serviceName, method, path, config);
    const currentClient = this._client();
    return currentClient(initParams);
  }

  public async get<P, H>(serviceName: string, path: string, config: IApiGetConfiguration<P, H>) {
    return this.ajax(serviceName, 'GET', path, config);
  }

  public async post<P, H, B>(
    serviceName: string,
    path: string,
    config: IApiPostConfiguration<P, H, B>,
  ) {
    return this.ajax(serviceName, 'POST', path, config);
  }

  public async put<P, H>(serviceName: string, path: string, config: IApiPutConfiguration<P, H>) {
    return this.ajax(serviceName, 'PUT', path, config);
  }

  public async patch<P, H>(
    serviceName: string,
    path: string,
    config: IApiPatchConfiguration<P, H>,
  ) {
    return this.ajax(serviceName, 'PATCH', path, config);
  }

  public async delete<P, H>(
    serviceName: string,
    path: string,
    config: IApiDeleteConfiguration<P, H>,
  ) {
    return this.ajax(serviceName, 'DELETE', path, config);
  }

  public async head<P, H>(serviceName: string, path: string, config: IApiGetConfiguration<P, H>) {
    return this.ajax(serviceName, 'HEAD', path, config);
  }
}

export function isAxiosError(error: unknown) {
  return axios.isAxiosError(error);
}
